// src/pages/public/ElectionVotingPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../../../components/layout/PublicLayout';
import Button from '../../../components/common/Button';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';

const ElectionVotingPage = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();

  // State
  const [step, setStep] = useState('verification'); // verification, voting, success
  const [school, setSchool] = useState(null);
  const [credential, setCredential] = useState('');
  const [student, setStudent] = useState(null);
  const [election, setElection] = useState(null);
  const [results, setResults] = useState(null);
  const [votes, setVotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSchoolData();
  }, [schoolId]);

  // Auto-refresh results every 3 seconds when on voting page
  useEffect(() => {
    if (step === 'voting' && election) {
      fetchResults();
      const interval = setInterval(fetchResults, 3000);
      return () => clearInterval(interval);
    }
  }, [step, election]);

  const fetchSchoolData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/schools/${schoolId}`);
      const data = await response.json();
      if (data.success) {
        setSchool(data.data);
      }
    } catch (error) {
      console.error('Error fetching school:', error);
      setError('Failed to load school information');
    }
  };

  const fetchResults = async () => {
    if (!election) return;
    try {
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/elections/${election.id}/results`);
      const data = await response.json();
      if (data.success) {
        setResults(data.data);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Verify student
      const verifyResponse = await fetch(`${API_BASE_URL_ELECTIONS}/students/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schoolId, credential })
      });
      const verifyData = await verifyResponse.json();

      if (!verifyData.success) {
        setError(verifyData.message || 'Invalid credentials');
        setLoading(false);
        return;
      }

      setStudent(verifyData.data);

      // Fetch active elections
      const electionResponse = await fetch(`${API_BASE_URL_ELECTIONS}/elections/school/${schoolId}/active`);
      const electionData = await electionResponse.json();

      if (electionData.success && electionData.data.length > 0) {
        setElection(electionData.data[0]);
        setStep('voting');
      } else {
        setError('No active elections found');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVoteSelect = (positionId, candidateId) => {
    setVotes(prev => ({
      ...prev,
      [positionId]: candidateId
    }));
  };

  // const handleSubmitVotes = async () => {
  //   setLoading(true);
  //   setError('');

  //   try {
  //     const votesArray = Object.entries(votes).map(([positionId, candidateId]) => ({
  //       positionId,
  //       candidateId
  //     }));

  //     const response = await fetch(`${API_BASE_URL_ELECTIONS}/votes/cast-multiple`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({
  //         studentId: student.id,
  //         electionId: election.id,
  //         votes: votesArray
  //       })
  //     });

  //     const data = await response.json();

  //     if (data.success) {
  //       setStep('success');
  //     } else {
  //       setError(data.message || 'Failed to submit votes');
  //     }
  //   } catch (error) {
  //     console.error('Submit votes error:', error);
  //     setError('Failed to submit votes. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const handleSubmitVotes = async () => {
  // Count how many positions exist and how many the user voted for
  const totalPositions = Object.keys(election.positions || {}).length;
  const votedCount = Object.keys(votes).length;

  // Check if user missed any positions
  if (votedCount < totalPositions) {
    const confirmSubmit = window.confirm(
      "⚠️ You haven’t voted for all positions.\n\nOnce you submit, you won’t be able to vote again.\n\nDo you still want to submit your votes?"
    );
    if (!confirmSubmit) return; // Cancel submission if user says no
  } else {
    const confirmSubmit = window.confirm(
      "✅ You are about to submit your votes.\n\nOnce you submit, you won’t be able to vote again.\n\nDo you want to continue?"
    );
    if (!confirmSubmit) return;
  }

  setLoading(true);
  setError('');

  try {
    const votesArray = Object.entries(votes).map(([positionId, candidateId]) => ({
      positionId,
      candidateId
    }));

    const response = await fetch(`${API_BASE_URL_ELECTIONS}/votes/cast-multiple`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        studentId: student.id,
        electionId: election.id,
        votes: votesArray
      })
    });

    const data = await response.json();

    if (data.success) {
      setStep('success');
    } else {
      setError(data.message || 'Failed to submit votes');
    }
  } catch (error) {
    console.error('Submit votes error:', error);
    setError('Failed to submit votes. Please try again.');
  } finally {
    setLoading(false);
  }
};


  const getCandidateVotes = (positionId, candidateId) => {
    const position = results?.results?.find(r => r.position.id === positionId);
    const candidate = position?.candidates?.find(c => c.id === candidateId);
    return {
      votes: candidate?.voteCount || 0,
      percentage: candidate?.percentage || 0,
      isLeading: position?.candidates?.[0]?.id === candidateId && candidate?.voteCount > 0
    };
  };

  const getVerificationLabel = () => {
    if (!school) return 'Verification Code';
    switch (school.verificationType) {
      case 'PHONE_NUMBER': return 'Phone Number';
      case 'EMAIL': return 'Email Address';
      case 'INDEX_NUMBER': return 'Index Number';
      case 'UNIQUE_CODE': return 'Verification Code';
      default: return 'Verification Code';
    }
  };

  const getVerificationPlaceholder = () => {
    if (!school) return 'Enter your code';
    switch (school.verificationType) {
      case 'PHONE_NUMBER': return '0241234567';
      case 'EMAIL': return 'your.email@school.edu';
      case 'INDEX_NUMBER': return 'UG00001';
      case 'UNIQUE_CODE': return 'ABC123XYZ';
      default: return 'Enter your code';
    }
  };

  // Render Verification Step
  if (step === 'verification') {
    return (
      <PublicLayout>
        <div className="min-h-screen bg-black py-12">
          <div className="max-w-md mx-auto px-4">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl shadow-2xl p-8">
                {/* School Header */}
                <div className="text-center mb-8">
                  {school?.logo ? (
                    <img src={school.logo} alt={school.name} className="h-20 w-20 mx-auto mb-4 rounded-full object-cover border-2 border-red-500/30" />
                  ) : (
                    <div className="h-20 w-20 mx-auto mb-4 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center text-white text-2xl font-bold border-2 border-red-500/30">
                      {school?.shortName || '?'}
                    </div>
                  )}
                  <h1 className="text-2xl font-bold text-white">{school?.name}</h1>
                  <p className="text-gray-400 mt-2">Student Verification</p>
                </div>

                {/* Verification Form */}
                <form onSubmit={handleVerification}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {getVerificationLabel()}
                    </label>
                    <input
                      type="text"
                      value={credential}
                      onChange={(e) => setCredential(e.target.value)}
                      placeholder={getVerificationPlaceholder()}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      required
                      disabled={loading}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Enter your {getVerificationLabel().toLowerCase()} to verify your identity
                    </p>
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-3 rounded-lg shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105"
                    disabled={loading}
                  >
                    {loading ? 'Verifying...' : 'Verify & Continue'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => navigate('/elections')}
                    className="text-sm text-red-400 hover:text-red-300 transition-colors"
                  >
                    ← Back to Schools
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Render Voting Step with Real-Time Results
  if (step === 'voting') {
    return (
      <PublicLayout>
        <div className="min-h-screen bg-black py-12">
          <div className="max-w-7xl mx-auto px-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border border-red-900/30 rounded-2xl shadow-2xl p-8 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-4xl font-black text-white mb-2">{election?.title}</h1>
                  <p className="text-gray-400">{election?.description}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="px-4 py-2 bg-red-600 text-white rounded-full text-sm font-bold shadow-lg shadow-red-600/50 animate-pulse flex items-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                    LIVE
                  </span>
                  <span className="text-gray-400 text-sm">
                    Voting as: <span className="font-semibold text-red-400">{student?.fullName}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Positions */}
            <div className="space-y-8">
              {election?.positions?.map((position) => {
                const positionResults = results?.results?.find(r => r.position.id === position.id);
                const totalVotes = positionResults?.totalVotes || 0;

                return (
                  <div key={position.id} className="group relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
                    <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl shadow-2xl overflow-hidden">
                      {/* Position Header */}
                      <div className="bg-gradient-to-r from-gray-800 via-gray-900 to-black p-6 border-b border-red-900/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <h2 className="text-3xl font-bold text-white mb-1">{position.title}</h2>
                            {position.description && (
                              <p className="text-gray-400">{position.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-3xl font-black text-red-500">{totalVotes}</div>
                            <div className="text-gray-400 text-sm">Total Votes</div>
                          </div>
                        </div>
                      </div>

                      {/* Candidates Grid */}
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {position.candidates?.map((candidate) => {
                          const stats = getCandidateVotes(position.id, candidate.id);
                          const isSelected = votes[position.id] === candidate.id;

                          return (
                            <button
                              key={candidate.id}
                              onClick={() => handleVoteSelect(position.id, candidate.id)}
                              className={`relative group/card overflow-hidden rounded-xl transition-all duration-300 ${
                                isSelected
                                  ? 'ring-4 ring-red-500 scale-105 shadow-2xl shadow-red-600/50'
                                  : 'hover:scale-105 hover:shadow-xl'
                              }`}
                            >
                              {/* Card Content */}
                              <div className={`relative ${
                                isSelected 
                                  ? 'bg-gradient-to-br from-red-600 to-red-700' 
                                  : stats.isLeading
                                  ? 'bg-gradient-to-br from-yellow-500 to-orange-600'
                                  : 'bg-gradient-to-br from-gray-800 to-gray-900'
                              } p-6 text-white border ${
                                isSelected ? 'border-red-400' : stats.isLeading ? 'border-yellow-400' : 'border-gray-700'
                              }`}>
                                
                                {/* Leading Badge */}
                                {stats.isLeading && !isSelected && (
                                  <div className="absolute top-2 right-2 bg-white text-orange-600 px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-bounce flex items-center gap-1">
                                    <span>👑</span>
                                    <span>LEADING</span>
                                  </div>
                                )}

                                {/* Selected Badge */}
                                {isSelected && (
                                  <div className="absolute top-2 right-2 bg-white text-red-600 px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                                    <span>✓</span>
                                    <span>SELECTED</span>
                                  </div>
                                )}

                                {/* Candidate Photo */}
                                <div className="relative mb-4">
                                  {candidate.photo ? (
                                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-white shadow-2xl">
                                      <img 
                                        src={candidate.photo} 
                                        alt={candidate.fullName}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-32 h-32 mx-auto rounded-full bg-white/20 backdrop-blur-sm border-4 border-white shadow-2xl flex items-center justify-center text-5xl font-bold">
                                      {candidate.fullName.charAt(0)}
                                    </div>
                                  )}
                                </div>

                                {/* Candidate Info */}
                                <div className="text-center mb-4">
                                  <h3 className="text-2xl font-bold mb-2">{candidate.fullName}</h3>
                                  {candidate.bio && (
                                    <p className="text-sm opacity-90 mb-3">{candidate.bio}</p>
                                  )}
                                  {candidate.manifesto && (
                                    <p className="text-xs opacity-75 italic">"{candidate.manifesto}"</p>
                                  )}
                                </div>

                                {/* Vote Statistics */}
                                <div className="space-y-2">
                                  {/* Progress Bar */}
                                  <div className="bg-white/20 rounded-full h-3 overflow-hidden backdrop-blur-sm">
                                    <div 
                                      className="bg-white h-full rounded-full transition-all duration-1000 ease-out"
                                      style={{ width: `${stats.percentage}%` }}
                                    ></div>
                                  </div>
                                  
                                  {/* Stats Row */}
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="font-bold text-2xl">{stats.votes}</span>
                                    <span className="font-black text-2xl">{stats.percentage}%</span>
                                  </div>
                                </div>

                                {/* Hover Effect Overlay */}
                                <div className={`absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity pointer-events-none`}></div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-900/20 border border-red-500/50 rounded-xl text-red-400 text-center font-semibold shadow-lg">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-8 sticky bottom-4 z-50">
              <Button
                onClick={handleSubmitVotes}
                variant="primary"
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-black text-xl py-6 rounded-2xl shadow-2xl shadow-red-600/50 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || Object.keys(votes).length === 0}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    Submitting...
                  </span>
                ) : (
                  `🗳️ SUBMIT ${Object.keys(votes).length} VOTE(S)`
                )}
              </Button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Render Success Step
  if (step === 'success') {
    return (
      <PublicLayout>
        <div className="min-h-screen bg-black py-12 flex items-center justify-center">
          <div className="max-w-md mx-auto px-4">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl opacity-40 blur-xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-3xl shadow-2xl p-12 text-center overflow-hidden">
                {/* Confetti Effect */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute text-2xl animate-ping"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 2}s`,
                        animationDuration: '2s'
                      }}
                    >
                      🎉
                    </div>
                  ))}
                </div>

                <div className="relative z-10">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl animate-bounce">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  
                  <h1 className="text-4xl font-black text-white mb-4">Vote Submitted!</h1>
                  <p className="text-xl text-gray-400 mb-8">
                    Your vote has been successfully recorded and counted. Thank you for participating!
                  </p>
                  
                  <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4 mb-8">
                    <p className="text-sm text-gray-300">
                      <span className="font-bold text-green-400">🔒 Secure:</span> Your vote is encrypted and anonymous
                    </p>
                  </div>

                  <Button
                    onClick={() => navigate('/elections')}
                    variant="primary"
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold text-lg py-4 rounded-xl shadow-lg shadow-green-600/30 transition-all duration-300 transform hover:scale-105"
                  >
                    Back to Elections
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return null;
};

export default ElectionVotingPage;